<template>
    <b-container fluid>
        <validation-observer ref="rooms_form" v-slot="{ meta }">
            <div v-if="wizardData" class="mb-3">
                <div
                    class="
                        d-flex
                        justify-content-between
                        align-items-center
                        pt-3
                    "
                >
                    <div>
                        <b>{{ wizardData.name }} - Inn Rooms</b>
                    </div>
                </div>
                <hr />
            </div>
            <div v-if="roomsInfo">
                <div class="d-flex align-items-center mb-4">
                    <span class="mr-2">
                        <b>Show all rooms on online booking calendar: </b>
                    </span>
                    <form-square-switch
                        class="all-rooms-switch"
                        :labels="{
                            checked: 'ON',
                            unchecked: 'OFF',
                        }"
                        :sync="true"
                        v-model="roomsInfo.rpsettings.show_all_rooms"
                        :isToolbar="true"
                        @input="setShowRooms"
                    />
                </div>
                <p>
                    Set this option in case you want to display all of the
                    individual unit/room titles on online booking calendar.
                    <br />
                    Otherwise only general unit/room category will be displayed.
                </p>
                <div
                    v-if="!roomsInfo.rpsettings.show_all_rooms"
                    class="mb-4 mt-3"
                >
                    <p><b>Reservation Logic </b></p>
                    <p>
                        When only the general property or room type is displayed
                        on the online booking calendar, then the application
                        must have some rules on how to assign guests to a
                        particular property/room. The property owner must decide
                        which rules work best for his/her property.
                    </p>
                    <p>
                        All reservations remain fixed once assigned to
                        property/room. Application will assign rooms/properties
                        according to rules below.
                    </p>
                    <b-form-radio
                        class="mb-2 ml-2"
                        v-model="roomsInfo.rpsettings.occupancy_type"
                        name="rule"
                        value="FIXED_RULE_A"
                        @change="setOccupancyType"
                        >Rule A: Calculate time from previous guest check-out to
                        new guest check-in. Choose room that will maximize
                        occupancy.</b-form-radio
                    >
                    <b-form-radio
                        class="mb-2 ml-2"
                        v-model="roomsInfo.rpsettings.occupancy_type"
                        name="rule"
                        value="FIXED_RULE_B"
                        @change="setOccupancyType"
                        >Rule B: Calculate time from new guest check-out to next
                        guest check-in. Choose room that will maximize
                        occupancy.</b-form-radio
                    >
                    <b-form-radio
                        class="mb-2 ml-2"
                        v-model="roomsInfo.rpsettings.occupancy_type"
                        name="rule"
                        value="FIXED_RULE_C"
                        @change="setOccupancyType"
                        >Rule C: Compare results of Rule A versus Rule B. Choose
                        room that will maximize occupancy.</b-form-radio
                    >
                </div>

                <b-row class="pt-2">
                    <b-col lg="8">
                        <b>Inn Rooms</b>
                        <hr />
                        <b-row>
                            <b-col md="9">
                                <validation-provider
                                    name="qty"
                                    v-slot="{ errors, handleChange }"
                                    rules="validInteger"
                                    
                                >
                                    <form-input
                                        :leftIcon="['fas', 'bed']"
                                        v-model="roomsInfo.qty"
                                        :error="errors[0]"
                                        floatingLabel="Quantity"
                                        @input="handleChange"
                                /></validation-provider>
                            </b-col>
                            <b-col md="3">
                                <b-button
                                    class="set-room-qty-button mb-3 mb-md-0"
                                    variant="primary"
                                    :disabled="!meta.valid"
                                    @click="setRoomQty"
                                    >Set Room Quantity</b-button
                                >
                            </b-col>
                        </b-row>
                        <grid
                            :rowData="roomsInfo.rooms"
                            :isSearch="false"
                            :isAutoHeight="true"
                            :columnDefs="columnDefs"
                            :isAutoWidth="true"
                        />
                    </b-col>
                </b-row>
            </div>
            <modal
                id="error-modal"
                title="Error"
                @hidden="error = null"
                ok-only
            >
                {{ error }}
            </modal>
        </validation-observer>
    </b-container>
</template>

<script>
import { mapGetters } from 'vuex';
import API from '../../../../api';
import { RENTAL_PROPERTY_SETUP_ROOMS_ENDPOINT } from '../../../../endpoints';
import { formatResponseError } from '../../../../utils/api';
import FormSquareSwitch from '../../../generic/form-square-switch/form-square-switch.vue';
import FormInput from '../../../generic/form-input/form-input.vue';
import grid from '../../../generic/grid/grid.vue';
import Modal from '../../../generic/modal/modal.vue';
import isArray from 'lodash/isArray';
import StatusCellRenderer from '../roomtypes/status-cell-renderer.vue';

export default {
    components: {
        FormSquareSwitch,
        FormInput,
        grid,
        Modal,
        statusCellRenderer: StatusCellRenderer,
    },
    inject: ['propertyId'],
    data() {
        return {
            roomsInfo: null,
            error: null,
        };
    },
    computed: {
        ...mapGetters('setupPropertyStore', ['wizardData']),
        columnDefs() {
            return [
                {
                    field: 'name',
                    headerName: 'Room Name',
                    type: 'editableColumn',
                    autoWidthExclude: true,
                    cellClass: 'lx-editable-cell text-primary',
                    valueSetter: async ({ node, data, newValue, oldValue }) => {
                        (async () => {
                            try {
                                await API.post(
                                    `${RENTAL_PROPERTY_SETUP_ROOMS_ENDPOINT(
                                        this.propertyId
                                    )}set-room-name/`,
                                    {
                                        name: newValue,
                                        room_pk: data.id,
                                    }
                                );
                                this.successToast({
                                    text: 'Room name successfully changed',
                                });
                            } catch (e) {
                                data.name = oldValue;
                                node.setData(data);
                            }
                        })();
                        data.name = newValue;
                        return true;
                    },
                },
                {
                    field: 'disabled',
                    headerName: 'Status',
                    autoWidthExclude: true,
                    valueGetter: ({ data }) => !data.disabled,
                    cellRenderer: 'statusCellRenderer',
                    cellClass: 'd-flex justify-content-center',
                    headerClass: 'ag-centered-header',
                    cellRendererParams: {
                        onChange: async ({ data, node, api }, value) => {
                            try {
                                await API.post(
                                    `${RENTAL_PROPERTY_SETUP_ROOMS_ENDPOINT(
                                        this.propertyId
                                    )}toggle-room-status/`,
                                    { value: !value, room_pk: data.id }
                                );
                                await this.fetchRooms();
                                this.successToast({
                                    text: 'Room status successfully changed.',
                                });
                            } catch (e) {
                                const { generic } = formatResponseError(e);
                                this.errorToast({
                                    text:
                                        generic ||
                                        'Could not change room status',
                                });
                                api.redrawRows({ rowNodes: [node] });
                                return;
                            }
                        },
                    },
                },
                // {
                //     field: 'disabled',
                //     headerName: 'Enabled',
                //     type: 'switchColumn',
                //     valueGetter: ({ data }) => !data.disabled,
                //     cellRendererParams: {
                //         onChange: async ({ node, api, data }, newValue) => {
                //             try {
                //                 await API.post(
                //                     `${RENTAL_PROPERTY_SETUP_ROOMS_ENDPOINT(
                //                         this.propertyId
                //                     )}toggle-room-status/`,
                //                     {
                //                         value: !newValue,
                //                         room_pk: data.id,
                //                     }
                //                 );
                //                 await this.fetchRooms();
                //                 this.successToast({
                //                     text: 'Room status successfully changed.',
                //                 });
                //             } catch (e) {
                //                 this.errorToast({
                //                     text: `Could not enable room.`,
                //                 });
                //                 api.redrawRows({ rowNodes: [node] });
                //                 return;
                //             }
                //         },
                // },
                // },
            ];
        },
    },
    mounted() {
        this.fetchRooms();
    },
    methods: {
        async fetchRooms() {
            try {
                this.$parent.$emit('busy', true);
                const { data } = await API.get(
                    RENTAL_PROPERTY_SETUP_ROOMS_ENDPOINT(this.propertyId)
                );
                this.roomsInfo = data;
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not fetch rooms' });
            } finally {
                this.$parent.$emit('busy', false);
            }
        },
        async setRoomQty() {
            try {
                this.$parent.$emit('busy', true);
                await API.post(
                    `${RENTAL_PROPERTY_SETUP_ROOMS_ENDPOINT(
                        this.propertyId
                    )}set-rooms-qty/`,
                    {
                        qty: this.roomsInfo.qty,
                    }
                );
                await this.fetchRooms();
                this.successToast({ text: 'Room quantity succesfuly set.' });
            } catch (e) {
                const { generic } = formatResponseError(e);
                if (generic) {
                    const error = isArray(generic) ? generic[0] : generic;
                    this.error = error;
                    this.$bvModal.show('error-modal');
                } else {
                    this.errorToast({ text: 'Could not fetch rooms' });
                }
            } finally {
                this.$parent.$emit('busy', false);
            }
        },
        async setOccupancyType(value) {
            try {
                this.$parent.$emit('busy', true);
                await API.post(
                    `${RENTAL_PROPERTY_SETUP_ROOMS_ENDPOINT(
                        this.propertyId
                    )}set-occupancy-type/`,
                    {
                        occupancy_type: value,
                    }
                );
                this.successToast({
                    text: 'Occupancy settings successfully changed.',
                });
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({
                    text: generic || 'Could not set occupancy settings',
                });
            } finally {
                this.$parent.$emit('busy', false);
            }
        },
        async setShowRooms() {
            try {
                this.$parent.$emit('busy', true);
                await API.post(
                    `${RENTAL_PROPERTY_SETUP_ROOMS_ENDPOINT(
                        this.propertyId
                    )}toggle-show-rooms/`
                );
                this.successToast({
                    text: 'All rooms setting successfully changed.',
                });
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({
                    text: generic || 'Could not set show rooms setting',
                });
            } finally {
                this.$parent.$emit('busy', false);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.all-rooms-switch {
    width: 120px;
}
.set-room-qty-button {
    height: 39px;
}
</style>